/**
 * Welcome pop-up on product pages (sections/cl-welcome.liquid).
 * - Opens once per visitor, `data-delay` ms after load (1 s, Cris 26 Oct); on the homepage after the first
 *   scroll instead (`data-after-scroll` px, Cris 3 Nov); waits while the cookie banner or another dialog is open. Closed → not again for 30 days; signed up → never again.
 * - "Which WHOOP do you have?" answers whether this product fits (data-models = fit.models from
 *   snippets/cl-compat.liquid) and remembers the model in localStorage cl-whoop-model (shared with the
 *   quiz and the fit check). Nothing is said for a product without fit.models.
 * - Email: native Shopify customer form (page reloads; hCaptcha needs it). Before submit we set a session flag;
 *   when the page comes back with ?customer_posted=true an inline script in the section opens the code step at
 *   once (3 Nov), and this module then just records the signup.
 * Events: welcome_shown, compatibility_selected { model, source }, welcome_signup { model }.
 */

const SEEN_KEY = 'cl-welcome-v1';
const MODEL_KEY = 'cl-whoop-model';
const SUBMIT_KEY = 'cl-welcome-submitted';
const SNOOZE_DAYS = 30;

const clTrack = (name, data) => {
  try {
    // @ts-ignore Shopify analytics global; custom events reach Customer Events pixels
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (e) {}
};

const read = (store, key) => {
  try {
    return store.getItem(key);
  } catch (e) {
    return null;
  }
};
const write = (store, key, value) => {
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch (e) {}
};

class ClaspLabWelcome extends HTMLElement {
  /** @type {HTMLDialogElement | null} */ dialog = null;
  #model = '';

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const chip = target?.closest('[data-model]');
      if (chip instanceof HTMLElement) this.#pick(chip, true);
      if (target?.closest('[data-close]')) this.#close();
      if (target?.closest('[data-copy]')) this.#copy();
    });
    // click on the dimmed area closes
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.#close();
    });
    this.dialog.addEventListener('cancel', () => this.#snooze());
    const form = this.querySelector('[data-welcome-form]');
    const join = form?.querySelector('.cl-welcome__join');
    const joinText = join?.textContent || '';
    // The native submit stays (Shopify's hCaptcha only runs on it; a fetch/form.submit() version broke signups 3 Nov).
    // The button answers at once so the check + reload read as "working", not as the site hanging.
    form?.addEventListener('submit', () => {
      write(sessionStorage, SUBMIT_KEY, '1');
      clTrack('welcome_signup', { model: this.#model });
      if (join) {
        join.textContent = 'One moment…';
        join.setAttribute('aria-busy', 'true');
      }
    });
    // back/forward cache: don't come back to a button stuck on "One moment…"
    window.addEventListener('pageshow', () => {
      if (!join || join.getAttribute('aria-busy') !== 'true') return;
      join.textContent = joinText;
      join.removeAttribute('aria-busy');
    });

    // remembered model (quiz, fit check, earlier pop-up)
    const saved = read(localStorage, MODEL_KEY);
    const chip = saved ? this.querySelector(`[data-key="${CSS.escape(saved)}"]`) : null;
    if (chip instanceof HTMLElement) this.#pick(chip, false);

    const params = new URLSearchParams(location.search);
    const justJoined = params.get('customer_posted') === 'true' && read(sessionStorage, SUBMIT_KEY);
    const formError = this.querySelector('[data-form-error]');

    if (justJoined) {
      write(sessionStorage, SUBMIT_KEY, null);
      write(localStorage, SEEN_KEY, JSON.stringify({ joined: Date.now() }));
      this.#step('done');
      this.#open(false);
      return;
    }
    if (formError && read(sessionStorage, SUBMIT_KEY)) {
      write(sessionStorage, SUBMIT_KEY, null);
      this.#open(false);
      return;
    }
    if (this.hasAttribute('data-design-mode')) return;
    if (!this.#due()) return;
    if (this.dataset.afterScroll) this.#afterScroll(Number(this.dataset.afterScroll) || 200);
    else setTimeout(() => this.#openWhenFree(), this.#delay());
  }

  /**
   * Homepage (Cris, 3 Nov: "after first scroll is okay too"): wait until the visitor has scrolled `px`, then open as
   * usual (still after the cookie banner). Listens in the capture phase so it also hears a scrolling page wrapper.
   * @param {number} px
   */
  #afterScroll(px) {
    const check = (/** @type {Event} */ event) => {
      const el = event.target instanceof Element ? event.target : null;
      const y = Math.max(window.scrollY, el ? el.scrollTop : 0);
      if (y < px) return;
      document.removeEventListener('scroll', check, { capture: true });
      setTimeout(() => this.#openWhenFree(), 400);
    };
    document.addEventListener('scroll', check, { capture: true, passive: true });
  }

  /**
   * The section's delay (1 s since 26 Oct, Cris: "put the pop-up back to the very start"). The 45 s wait on the page a
   * Meta ad opened (26 Oct) went the same day: signups fell from 2–4 a day to none, as most ad visitors leave sooner.
   */
  #delay() {
    return Number(this.dataset.delay) || 1000;
  }

  /** true if this visitor hasn't signed up and hasn't closed it in the last 30 days */
  #due() {
    try {
      const seen = JSON.parse(read(localStorage, SEEN_KEY) || 'null');
      if (!seen) return true;
      if (seen.joined) return false;
      return Date.now() - (seen.closed || 0) > SNOOZE_DAYS * 864e5;
    } catch (e) {
      return true;
    }
  }

  #busy() {
    const banner = document.getElementById('shopify-pc__banner');
    const prefs = document.getElementById('shopify-pc__prefs');
    // fixed elements have no offsetParent, so check display + layout boxes
    const visible = (el) => el instanceof HTMLElement && getComputedStyle(el).display !== 'none' && el.getClientRects().length > 0;
    if (visible(banner) || visible(prefs)) return true;
    if (document.querySelector('dialog[open]:not(.cl-welcome)')) return true;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return true;
    return false;
  }

  #openWhenFree(tries = 0) {
    if (!this.#due()) return;
    if (this.#busy()) {
      if (tries < 120) setTimeout(() => this.#openWhenFree(tries + 1), 1000);
      return;
    }
    this.#open(true);
  }

  /** @param {boolean} track */
  #open(track) {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    // don't pop the keyboard or a focus ring on open; the close button gets focus
    this.querySelector('[data-close]')?.focus({ preventScroll: true });
    if (track) clTrack('welcome_shown', {});
  }

  #close() {
    this.#snooze();
    this.dialog?.close();
  }

  #snooze() {
    try {
      const seen = JSON.parse(read(localStorage, SEEN_KEY) || 'null');
      if (seen?.joined) return;
    } catch (e) {}
    write(localStorage, SEEN_KEY, JSON.stringify({ closed: Date.now() }));
  }

  /** @param {'ask' | 'done'} name */
  #step(name) {
    this.querySelectorAll('[data-step]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = el.dataset.step !== name;
    });
  }

  /** @param {HTMLElement} chip @param {boolean} save */
  #pick(chip, save) {
    const model = chip.dataset.model || '';
    const key = chip.dataset.key || '';
    this.#model = key;
    this.querySelectorAll('[data-model]').forEach((b) => b.setAttribute('aria-pressed', String(b === chip)));

    const tags = this.querySelector('[data-tags]');
    if (tags instanceof HTMLInputElement) tags.value = `newsletter, welcome-popup, ${key}`;

    this.#answer(model);
    if (save) {
      write(localStorage, MODEL_KEY, key);
      clTrack('compatibility_selected', { model: key, source: 'welcome_popup' });
    }
  }

  /** Fit answer for this product, only from fit.models */
  #answer(model) {
    const out = this.querySelector('[data-fit]');
    if (!(out instanceof HTMLElement)) return;
    /** @type {string[]} */
    let models = [];
    try {
      models = JSON.parse(this.dataset.models || '[]');
    } catch (e) {}
    if (!models.length) {
      out.hidden = true;
      return;
    }
    const kind = this.dataset.kind || 'product';
    const check =
      '<svg class="cl-welcome__fit-icon" viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M4.5 10.5l3.5 3.5 7.5-8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    if (models.includes(model)) {
      out.innerHTML = `${check}<span>${kind === 'Claspie' ? `This Claspie fits your WHOOP ${model} band.` : `This ${kind} fits your WHOOP ${model}.`}</span>`;
    } else {
      const charms = this.querySelector('[data-charms-url]')?.getAttribute('data-charms-url') || '/collections/charms';
      const list = models.join(' and ');
      out.innerHTML = `<span>This ${kind} is made for WHOOP ${list}. Your WHOOP ${model} can wear every Claspie. <a href="${charms}">Shop Claspies</a></span>`;
    }
    out.hidden = false;
  }

  async #copy() {
    const code = this.dataset.code || '';
    const button = this.querySelector('[data-copy]');
    try {
      await navigator.clipboard.writeText(code);
      if (button) {
        button.textContent = 'Copied';
        setTimeout(() => (button.textContent = 'Copy'), 1600);
      }
    } catch (e) {}
  }
}

if (!customElements.get('cl-welcome')) customElements.define('cl-welcome', ClaspLabWelcome);
